import React from 'react';
import loadingIcon from 'src/assets/loading.gif';
import throttle from 'lodash/throttle';
import './style.scoped.css';

export enum ButtonTypes {
  primaryFilled = 'primaryFilled',
  primaryOutlined = 'primaryOutlined',
  primaryBorderless = 'primaryBorderless',
  primaryBorderless2 = 'primaryBorderless2',
  primaryBorderless3 = 'primaryBorderless3',
  secondaryFilled = 'secondaryFilled',
  secondaryOutlined = 'secondaryOutlined',
  secondaryBorderless = 'secondaryBorderless',
  errorBorderless = 'errorBorderless',
  outlinedNeutral = 'outlinedNeutral',
}

export enum ButtonSizes {
  small = 'small',
  normal = 'normal',
}

type ButtonProps = {
  text: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: (data?: any) => void;
  buttonType?: ButtonTypes;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: string;
  extraClassName?: string;
};

const Button = (props: ButtonProps) => {
  const {
    text,
    loading = false,
    onClick,
    buttonType = ButtonTypes.primaryFilled,
    disabled = false,
    leftIcon,
    rightIcon,
    size = 'normal',
    extraClassName,
    ...passProps
  } = props;

  const _handleClick = throttle((event: React.MouseEvent) => {
    if (loading || disabled) return;
    !!onClick && onClick();
  }, 500);

  const _getButtonBackgroundClassName = (): string => {
    switch (buttonType) {
      case ButtonTypes.primaryFilled:
      case ButtonTypes.secondaryFilled:
        return 'bg-secondary';
      case ButtonTypes.primaryOutlined:
      case ButtonTypes.primaryBorderless:
      case ButtonTypes.secondaryOutlined:
      case ButtonTypes.secondaryBorderless:
        return 'bg-surface';
      case ButtonTypes.primaryBorderless2:
        return 'bg-primaryLight';
      case ButtonTypes.primaryBorderless3:
        return 'bg-primaryLight2';
      case ButtonTypes.outlinedNeutral:
        return 'bg-background';
      case ButtonTypes.errorBorderless:
        return 'bg-errorLight';
      default:
        return 'bg-primary';
    }
  };

  const _getButtonBorderClassName = (): string => {
    switch (buttonType) {
      case ButtonTypes.primaryOutlined:
        return 'border border-primary';
      case ButtonTypes.secondaryOutlined:
        return 'border border-secondary';
      case ButtonTypes.outlinedNeutral:
        return 'border border-line';
      case ButtonTypes.primaryFilled:
      case ButtonTypes.primaryBorderless:
      case ButtonTypes.primaryBorderless2:
      case ButtonTypes.primaryBorderless3:
      case ButtonTypes.secondaryFilled:
      case ButtonTypes.secondaryBorderless:
      case ButtonTypes.errorBorderless:
      default:
        return 'border border-transparent';
    }
  };

  const _getButtonTextClassName = (): string => {
    switch (buttonType) {
      case ButtonTypes.primaryFilled:
      case ButtonTypes.secondaryFilled:
      case ButtonTypes.primaryOutlined:
      case ButtonTypes.primaryBorderless:
      case ButtonTypes.primaryBorderless2:
      case ButtonTypes.primaryBorderless3:
        return 'text-primary text-sm font-bold';
      case ButtonTypes.secondaryOutlined:
      case ButtonTypes.secondaryBorderless:
        return 'text-secondary text-sm font-bold';
      case ButtonTypes.errorBorderless:
        return 'text-error text-sm font-bold';
      case ButtonTypes.outlinedNeutral:
        return 'text-onSurface text-sm font-bold';
      default:
        return 'text-surface text-sm font-bold';
    }
  };

  const _getButtonSizeClassName = (): string => {
    switch (size) {
      case ButtonSizes.normal:
      case ButtonSizes.small:
        return 'h-11';
      default:
        return 'h-12';
    }
  };

  return (
    <button
      disabled={disabled}
      className={`${extraClassName} ${_getButtonBackgroundClassName()} ${_getButtonBorderClassName()} ${_getButtonTextClassName()} 
      ${disabled ? 'opacity-25' : ''} ${_getButtonSizeClassName()} button`}
      {...passProps}
      onClick={_handleClick}
    >
      {!!leftIcon && leftIcon}
      {loading ? (
        <img src={loadingIcon} className="w-6 h-6" alt={'loading'} />
      ) : (
        text
      )}
      {!!rightIcon && rightIcon}
    </button>
  );
};

export default Button;
